import { Mail, Plus, Star } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { TableCell, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { OrganisationRow } from '../query/types'
import { listMembers } from './api'
import type { CrmMemberList } from './types'

/**
 * The contact persons of an organisation under its row in the list (design §4.3): current members
 * first, former ones greyed out, with links to all of them and to adding one. Loads when it is
 * mounted, i.e. when the row is opened; `colSpan` is the width of the table.
 */
export function MemberRows({
  organisation,
  colSpan,
}: {
  organisation: Pick<OrganisationRow, 'id' | 'name'>
  colSpan: number
}) {
  const [list, setList] = useState<CrmMemberList | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    listMembers(organisation.id, { include_former: true, limit: 10 })
      .then((result) => {
        if (!cancelled) setList(result)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [organisation.id])

  const all = list ? list.current_count + list.former_count : 0
  return (
    <TableRow className="bg-muted/30 hover:bg-muted/30">
      <TableCell colSpan={colSpan} className="py-2 pl-12">
        {failed ? (
          <p className="text-sm text-destructive">Kon de contactpersonen niet laden.</p>
        ) : list === null ? (
          <Skeleton className="h-6 w-64" />
        ) : (
          <ul aria-label={`Contactpersonen van ${organisation.name}`} className="flex flex-col gap-1 text-sm">
            {list.items.map((member) => (
              <li
                key={member.id}
                className={cn('flex flex-wrap items-center gap-2', member.ended_on && 'text-muted-foreground')}
              >
                <EntityAvatar kind="person" name={member.full_name} size="xs" />
                <Link
                  to={`/relaties/contacten/${member.person_id}`}
                  className="font-medium underline-offset-2 hover:underline"
                >
                  {member.full_name}
                </Link>
                {member.role ? <span>· {member.role}</span> : null}
                {member.is_primary ? (
                  <span className="inline-flex items-center gap-1">
                    · <Star className="size-3.5 fill-current" aria-hidden /> primair
                  </span>
                ) : null}
                {member.ended_on ? <span>· voormalig</span> : null}
                {member.email ? (
                  <a
                    href={`mailto:${member.email}`}
                    aria-label={`E-mail ${member.full_name}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Mail className="size-3.5" aria-hidden />
                  </a>
                ) : null}
              </li>
            ))}
            {list.items.length === 0 ? (
              <li className="text-muted-foreground">Nog geen contactpersonen.</li>
            ) : null}
            {all > list.items.length ? (
              <li>
                <Link
                  to={`/relaties/organisaties/${organisation.id}?tab=contactpersonen`}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  Alle {all} →
                </Link>
              </li>
            ) : null}
            <li>
              <Link
                to={`/relaties/organisaties/${organisation.id}?tab=contactpersonen&toevoegen=1`}
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                <Plus className="size-3.5" aria-hidden />
                Contactpersoon toevoegen
              </Link>
            </li>
          </ul>
        )}
      </TableCell>
    </TableRow>
  )
}
